import FS from "@isomorphic-git/lightning-fs"
import git, {ReadCommitResult} from "isomorphic-git"
import {useCallback, useEffect, useState} from "react"
import * as Diff from "diff"

export const useGitDiff = (fs?: FS, gitDir?: string, mergeBranch?: string, baseBranch?: string) => {
  const [commits, setCommits] = useState<ReadCommitResult[]>([])
  const [diffString, setDiffString] = useState<string>()

  const getCommitDiff = useCallback(async (fs: FS, gitDir: string, branchMerge: string, branchBase: string) => {
    const mergeCommits = await git.log({
      fs,
      gitdir: gitDir,
      ref: branchMerge,
    })

    const baseCommits = await git.log({
      fs,
      gitdir: gitDir,
      ref: branchBase,
      depth: 1
    })

    const commits: ReadCommitResult[] = []
    for (const c of mergeCommits) {
      if (c.oid === baseCommits[0].oid) {
        break
      }
      commits.push(c)
    }
    setCommits(commits)
  }, [])

  const getBlobContent = useCallback(async (fs: FS, gitDir: string, oid: string): Promise<string> => {
    const result = await git.readBlob({
      fs,
      gitdir: gitDir,
      oid
    })
    return new TextDecoder().decode(result.blob)
  }, [])

  const getFileDiff = useCallback(async (
    fs: FS,
    gitDir: string,
    branchMerge: string,
    branchBase: string
  ): Promise<string> => {
    const result: { path: string, diffString: string | null }[] = await git.walk({
      fs,
      gitdir: gitDir,
      trees: [git.TREE({ref: branchBase}), git.TREE({ref: branchMerge})],
      map: async function (filepath, [A, B]) {
        if (filepath === ".") {
          return
        }

        if ((await A?.type()) === "tree" || (await B?.type()) === "tree") {
          return
        }

        // generate ids
        const Aoid = await A?.oid()
        const Boid = await B?.oid()

        // Get file status
        let type = "equal"
        if (Aoid == null) {
          type = "add"
        } else if (Boid == null) {
          type = "delete"
        } else if (Aoid !== Boid && Aoid !== null && Boid !== null) {
          type = "modified"
        }

        // Get blob content
        let contentA = ""
        let contentB = ""

        if (Aoid != null) {
          contentA = await getBlobContent(fs, gitDir, Aoid)
        }

        if (Boid != null) {
          contentB = await getBlobContent(fs, gitDir, Boid)
        }

        let diffString: string | null = Diff.createPatch(filepath, contentA, contentB).toString()
        diffString = diffString.split("\n").slice(4).join("\n")

        switch (type) {
          case "add":
            diffString = `diff --git a/${filepath} b/${filepath}` +
              `\nnew file mode 100644\nindex 0000000..${Boid}` +
              `\n--- /dev/null\n+++ b/${filepath}` +
              `\n${diffString}`
            break
          case "modified":
            diffString = `diff --git a/${filepath} b/${filepath}` +
              `\nindex ${Aoid}..${Boid}` +
              `\n--- a/${filepath}\n+++ b/${filepath}` +
              `\n${diffString}`
            break
          case "delete":
            diffString = `diff --git a/${filepath} b/${filepath}` +
              `\ndeleted file mode 100644\nindex ${Aoid}..0000000\n` +
              `--- a/${filepath}\n+++ /dev/null` +
              `\n${diffString}`
            break
          default:
            diffString = null
            break
        }

        return {
          path: `/${filepath}`,
          diffString,
        }
      },
    })
    return result
      .filter(r => r.diffString != null)
      .map(r => r.diffString)
      .join("\n")
  }, [getBlobContent])

  useEffect(() => {
    if (!fs || !gitDir || !mergeBranch || !baseBranch) {
      return
    }
    getCommitDiff(fs, gitDir, mergeBranch, baseBranch).then()
    getFileDiff(fs, gitDir, mergeBranch, baseBranch).then(res => {
      setDiffString(res)
    })
  }, [fs, gitDir, mergeBranch, baseBranch])

  return {
    commits,
    diffString
  }
}
